import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, Store, Sparkles } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { SectionTitle } from "@/components/Bits";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUploader } from "@/components/ImageUploader";

const field = "fk-border rounded-lg mt-1";

function useCategories() {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  return cats;
}

function VendorApplyForm() {
  const { t, L } = useLang();
  const [params] = useSearchParams();
  const cats = useCategories();
  const [markets, setMarkets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name_zh: "", name_en: "", category: "", bio_zh: "", contact_phone: "", contact_email: "",
    social: { instagram: "", facebook: "", whatsapp: "" }, photos: [], market_id: params.get("market") || "",
  });

  useEffect(() => {
    api.get("/markets").then((r) => setMarkets(r.data.filter((m) => m.apply_open))).catch(() => {});
  }, []);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setSocial = (k, v) => setForm({ ...form, social: { ...form.social, [k]: v } });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/vendors/apply", form);
      toast.success(L("摊主申请已提交，管理员批准后将显示在「市集摊主」。", "Vendor application submitted! It will appear under Vendors after admin approval."));
      setForm({ name_zh: "", name_en: "", category: "", bio_zh: "", contact_phone: "", contact_email: "", social: { instagram: "", facebook: "", whatsapp: "" }, photos: [], market_id: "" });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white fk-border-thick rounded-2xl p-6 sm:p-8 fk-shadow space-y-5 mt-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="text-sm font-medium">{L("摊位名称（中）", "Stall name (ZH)")} *</label><Input required value={form.name_zh} onChange={(e) => set("name_zh", e.target.value)} data-testid="va-name-zh" className={field} /></div>
        <div><label className="text-sm font-medium">{L("摊位名称（英）", "Stall name (EN)")}</label><Input value={form.name_en} onChange={(e) => set("name_en", e.target.value)} className={field} /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">{L("类别", "Category")} *</label>
          <Select value={form.category} onValueChange={(v) => set("category", v)}>
            <SelectTrigger className={field} data-testid="va-category"><SelectValue placeholder={L("选择类别", "Select category")} /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.name_zh}>{L(c.name_zh, c.name_en)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium">{t("select_market")}</label>
          <Select value={form.market_id} onValueChange={(v) => set("market_id", v)}>
            <SelectTrigger className={field} data-testid="va-market"><SelectValue placeholder={t("select_market")} /></SelectTrigger>
            <SelectContent>{markets.map((m) => <SelectItem key={m.id} value={m.id}>{L(m.title_zh, m.title_en)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><label className="text-sm font-medium">{L("摊位简介", "Bio")}</label><Textarea rows={3} value={form.bio_zh} onChange={(e) => set("bio_zh", e.target.value)} data-testid="va-bio" className={field} /></div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="text-sm font-medium">{t("phone")} *</label><Input required value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="va-phone" className={field} /></div>
        <div><label className="text-sm font-medium">{t("email")} *</label><Input required type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="va-email" className={field} /></div>
        <div><label className="text-sm font-medium">WhatsApp</label><Input placeholder="60123456789" value={form.social.whatsapp} onChange={(e) => setSocial("whatsapp", e.target.value)} className={field} /></div>
        <div><label className="text-sm font-medium">Instagram URL</label><Input value={form.social.instagram} onChange={(e) => setSocial("instagram", e.target.value)} className={field} /></div>
      </div>
      <div><label className="text-sm font-medium">{L("照片（可多张）", "Photos (multiple)")}</label><div className="mt-2"><ImageUploader value={form.photos} onChange={(v) => set("photos", v)} /></div></div>
      <button disabled={busy} data-testid="va-submit" className="w-full fk-border rounded-full px-6 py-3 font-semibold bg-[#FF5757] text-white fk-lift disabled:opacity-50 flex items-center justify-center gap-2">
        <Send size={16} /> {busy ? "…" : t("submit")}
      </button>
    </form>
  );
}

function ArtisanApplyForm() {
  const { t, L } = useLang();
  const cats = useCategories();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name_zh: "", name_en: "", category: "", bio_zh: "", contact_phone: "", contact_email: "",
    social: { instagram: "", facebook: "", whatsapp: "" }, photos: [],
  });
  const set = (k, v) => setForm({ ...form, [k]: v });
  const setSocial = (k, v) => setForm({ ...form, social: { ...form.social, [k]: v } });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/artisans/apply", form);
      toast.success(L("手作达人申请已提交，管理员批准后将显示在「手作达人」。", "Artisan application submitted! It will appear under Artisans after admin approval."));
      setForm({ name_zh: "", name_en: "", category: "", bio_zh: "", contact_phone: "", contact_email: "", social: { instagram: "", facebook: "", whatsapp: "" }, photos: [] });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white fk-border-thick rounded-2xl p-6 sm:p-8 fk-shadow space-y-5 mt-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="text-sm font-medium">{L("名称（中）", "Name (ZH)")} *</label><Input required value={form.name_zh} onChange={(e) => set("name_zh", e.target.value)} data-testid="aa-name-zh" className={field} /></div>
        <div><label className="text-sm font-medium">{L("名称（英）", "Name (EN)")}</label><Input value={form.name_en} onChange={(e) => set("name_en", e.target.value)} className={field} /></div>
      </div>
      <div>
        <label className="text-sm font-medium">{L("类别", "Category")} *</label>
        <Select value={form.category} onValueChange={(v) => set("category", v)}>
          <SelectTrigger className={field} data-testid="aa-category"><SelectValue placeholder={L("选择类别", "Select category")} /></SelectTrigger>
          <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.name_zh}>{L(c.name_zh, c.name_en)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><label className="text-sm font-medium">{L("简介", "Bio")}</label><Textarea rows={3} value={form.bio_zh} onChange={(e) => set("bio_zh", e.target.value)} data-testid="aa-bio" className={field} /></div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="text-sm font-medium">{t("phone")} *</label><Input required value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="aa-phone" className={field} /></div>
        <div><label className="text-sm font-medium">{t("email")} *</label><Input required type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="aa-email" className={field} /></div>
        <div><label className="text-sm font-medium">WhatsApp</label><Input placeholder="60123456789" value={form.social.whatsapp} onChange={(e) => setSocial("whatsapp", e.target.value)} className={field} /></div>
        <div><label className="text-sm font-medium">Instagram URL</label><Input value={form.social.instagram} onChange={(e) => setSocial("instagram", e.target.value)} className={field} /></div>
      </div>
      <div><label className="text-sm font-medium">{L("作品照片（可多张）", "Work photos (multiple)")}</label><div className="mt-2"><ImageUploader value={form.photos} onChange={(v) => set("photos", v)} /></div></div>
      <button disabled={busy} data-testid="aa-submit" className="w-full fk-border rounded-full px-6 py-3 font-semibold bg-[#00E5FF] fk-lift disabled:opacity-50 flex items-center justify-center gap-2">
        <Send size={16} /> {busy ? "…" : t("submit")}
      </button>
    </form>
  );
}

export default function MarketApply() {
  const { t, L } = useLang();
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-14">
      <SectionTitle tag="JOIN · 放空申请" title={t("apply_title")} />
      <p className="text-black/60 -mt-4 mb-6">{L("选择你要申请的身份，填写资料后由管理员审核。", "Choose how you'd like to join — submit your details for admin review.")}</p>

      <Tabs defaultValue="vendor">
        <TabsList className="fk-border rounded-full bg-white p-1 w-full grid grid-cols-2">
          <TabsTrigger value="vendor" data-testid="apply-tab-vendor" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white"><Store size={15} className="mr-1" />{t("vendor_application")}</TabsTrigger>
          <TabsTrigger value="artisan" data-testid="apply-tab-artisan" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white"><Sparkles size={15} className="mr-1" />{t("artisan_application")}</TabsTrigger>
        </TabsList>
        <TabsContent value="vendor"><VendorApplyForm /></TabsContent>
        <TabsContent value="artisan"><ArtisanApplyForm /></TabsContent>
      </Tabs>
    </div>
  );
}
